import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { usePortfolio } from "../../context/PortfolioContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { useToast } from "../../hooks/use-toast";
import { fmtINR } from "../../lib/format";
import { Save, Calculator, ShieldCheck, AlertTriangle, Wrench, Loader2 } from "lucide-react";

const DEFAULTS = {
  portfolioMTMPrimary: 0,
  portfolioMTMMutualFund: 0,
  totalCharges: 0,
  otherCrDr: 0,
  bookedProfitsSTCG: 0,
  bookedProfitsLTCG: 0,
  bookedProfitsSpeculation: 0,
  dividendReceived: 0,
  nonEquity: 0,
};

export default function PortfolioValues() {
  const { toast } = useToast();
  const { summary, reload } = usePortfolio();
  const [values, setValues] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);
  const [integrity, setIntegrity] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);

  const loadIntegrity = async () => {
    try {
      setIntegrity(await api.integrityCheck());
    } catch (e) {}
  };
  useEffect(() => { loadIntegrity(); }, []);

  const rebuild = async () => {
    if (!window.confirm(
      "This will recompute every member's capital, units, and avgNAV from the full transaction ledger. Safe to run — cannot lose data. Continue?"
    )) return;
    setRebuilding(true);
    try {
      const res = await api.rebuildBalances();
      toast({
        title: "Balances rebuilt",
        description: `Drift before ₹${res.before.drift.toFixed(2)} → after ₹${res.after.drift.toFixed(2)}`,
      });
      setIntegrity(res.after);
      await reload();
    } catch (e) {
      toast({ title: "Rebuild failed", description: e.response?.data?.detail || "Could not rebuild" });
    }
    setRebuilding(false);
  };

  useEffect(() => {
    if (summary) {
      setValues({
        portfolioMTMPrimary: summary.portfolioMTMPrimary || 0,
        portfolioMTMMutualFund: summary.portfolioMTMMutualFund || 0,
        totalCharges: summary.totalCharges || 0,
        otherCrDr: summary.otherCrDr || 0,
        bookedProfitsSTCG: summary.bookedProfitsSTCG?.amount || 0,
        bookedProfitsLTCG: summary.bookedProfitsLTCG?.amount || 0,
        bookedProfitsSpeculation: summary.bookedProfitsSpeculation?.amount || 0,
        dividendReceived: summary.dividendReceived?.amount || 0,
        nonEquity: summary.nonEquity?.amount ?? summary.nonEquity ?? 0,
      });
    }
  }, [summary]);

  const update = (k, v) => {
    const num = parseFloat(v);
    setValues((s) => ({ ...s, [k]: isNaN(num) ? 0 : num }));
  };

  // Preview (must mirror backend formula)
  const stcgPostTax = values.bookedProfitsSTCG * 0.8;
  const ltcgPostTax = values.bookedProfitsLTCG * 0.85;
  const specPostTax = values.bookedProfitsSpeculation * 0.7;
  const divPostTax = values.dividendReceived * 0.7;
  const nonEqPostTax = values.nonEquity * 0.8;

  const totalCapital = summary?.totalCapital || 0;
  const totalUnits = summary?.totalUnits || 0;
  const currentValue =
    totalCapital +
    values.portfolioMTMPrimary +
    values.portfolioMTMMutualFund -
    values.totalCharges -
    values.otherCrDr +
    stcgPostTax + ltcgPostTax + specPostTax + divPostTax +
    nonEqPostTax;
  const currentNAV = totalUnits > 0 ? currentValue / totalUnits : 0;
  const returnPct = totalCapital > 0 ? ((currentValue - totalCapital) / totalCapital) * 100 : 0;

  const save = async () => {
    setBusy(true);
    try {
      await api.updatePortfolioValues(values);
      toast({
        title: "Saved",
        description: `NAV recalculated to \u20b9${currentNAV.toFixed(4)}`,
      });
      await reload();
    } catch (e) {
      toast({ title: "Failed", description: e.response?.data?.detail || "Could not save" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-5">
      <div className="lg:col-span-3 bg-slate-900 border border-slate-800 rounded-xl p-5 md:p-6">
        <div className="text-sm font-semibold text-white">Update portfolio inputs</div>
        <div className="text-xs text-slate-500">
          These values feed the NAV formula. Total Capital & Units update automatically from transactions.
        </div>

        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <Row label="MTM (Primary)" k="portfolioMTMPrimary" values={values} onChange={update} />
          <Row label="MTM (Mutual Fund)" k="portfolioMTMMutualFund" values={values} onChange={update} />
          <Row label="Total Charges" k="totalCharges" values={values} onChange={update} />
          <Row label="Other Cr/Dr (deduction)" k="otherCrDr" values={values} onChange={update} hint="Subtracted from current value" />
          <Row label="Booked STCG (pre-tax)" k="bookedProfitsSTCG" values={values} onChange={update} hint="Post-tax 20% applied" />
          <Row label="Booked LTCG (pre-tax)" k="bookedProfitsLTCG" values={values} onChange={update} hint="Post-tax 15% applied" />
          <Row label="Speculation (pre-tax)" k="bookedProfitsSpeculation" values={values} onChange={update} hint="Post-tax 30% applied" />
          <Row label="Dividend (pre-tax)" k="dividendReceived" values={values} onChange={update} hint="Post-tax 30% applied" />
          <Row label="Non Equity (pre-tax)" k="nonEquity" values={values} onChange={update} hint="Post-tax 20% applied" />
        </div>

        <Button
          onClick={save}
          disabled={busy}
          data-testid="save-portfolio-values"
          className="mt-6 h-11 px-6 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold disabled:opacity-60"
        >
          <Save className="mr-2" size={16} /> {busy ? "Saving…" : "Save and recalculate NAV"}
        </Button>

        {/* Integrity health check + rebuild */}
        <div
          className={`mt-6 rounded-xl p-4 border ${
            integrity?.ok === false
              ? "bg-rose-500/10 border-rose-500/40"
              : "bg-emerald-500/5 border-emerald-500/30"
          }`}
          data-testid="integrity-card"
        >
          <div className="flex items-start gap-3">
            {integrity?.ok === false ? (
              <AlertTriangle size={18} className="text-rose-400 shrink-0 mt-0.5" />
            ) : (
              <ShieldCheck size={18} className="text-emerald-400 shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-semibold ${integrity?.ok === false ? "text-rose-300" : "text-emerald-300"}`}>
                {integrity == null
                  ? "Checking portfolio integrity…"
                  : integrity.ok
                  ? "Portfolio balances are consistent"
                  : "Drift detected between Total Capital and Cashflow"}
              </div>
              {integrity && (
                <div className="mt-1.5 grid grid-cols-3 gap-2 text-[11px] font-mono">
                  <div>
                    <div className="text-slate-500 uppercase tracking-wider">Net Cashflow</div>
                    <div className="text-slate-100 mt-0.5">{fmtINR(integrity.netCashflow)}</div>
                  </div>
                  <div>
                    <div className="text-slate-500 uppercase tracking-wider">Total Capital</div>
                    <div className="text-slate-100 mt-0.5">{fmtINR(integrity.totalCapital)}</div>
                  </div>
                  <div>
                    <div className="text-slate-500 uppercase tracking-wider">Drift</div>
                    <div className={`mt-0.5 ${Math.abs(integrity.drift) < 0.5 ? "text-emerald-400" : "text-rose-400"}`}>
                      {fmtINR(integrity.drift)}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Button
              onClick={rebuild}
              disabled={rebuilding}
              data-testid="rebuild-balances-btn"
              variant="ghost"
              className="text-xs h-8 px-3 border border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-200"
            >
              {rebuilding ? (
                <Loader2 size={12} className="mr-1.5 animate-spin" />
              ) : (
                <Wrench size={12} className="mr-1.5" />
              )}
              {rebuilding ? "Rebuilding…" : "Rebuild balances"}
            </Button>
          </div>
        </div>
      </div>

      <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-xl p-5 md:p-6 h-fit sticky top-6">
        <div className="flex items-center gap-2 text-emerald-400">
          <Calculator size={16} />
          <div className="text-sm font-semibold">Computed NAV Preview</div>
        </div>
        <div className="mt-4 pb-4 border-b border-slate-800">
          <div className="text-xs text-slate-500">Current NAV</div>
          <div className="text-3xl font-semibold text-white mt-1">
            ₹{currentNAV.toFixed(4)}
          </div>
          <div className={`text-xs mt-1 ${returnPct >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
            {returnPct >= 0 ? "+" : ""}
            {returnPct.toFixed(2)}% vs capital
          </div>
        </div>

        <dl className="mt-4 text-sm space-y-2">
          <KV k="Total Capital" v={fmtINR(totalCapital)} />
          <KV k="+ MTM Primary" v={fmtINR(values.portfolioMTMPrimary)} positive={values.portfolioMTMPrimary >= 0} />
          <KV k="+ MTM MF" v={fmtINR(values.portfolioMTMMutualFund)} positive={values.portfolioMTMMutualFund >= 0} />
          <KV k="− Charges" v={fmtINR(values.totalCharges)} />
          <KV k="− Other Cr/Dr" v={fmtINR(values.otherCrDr)} />
          <KV k="+ STCG (post-tax)" v={fmtINR(stcgPostTax)} />
          <KV k="+ LTCG (post-tax)" v={fmtINR(ltcgPostTax)} />
          <KV k="+ Speculation (post-tax)" v={fmtINR(specPostTax)} />
          <KV k="+ Dividend (post-tax)" v={fmtINR(divPostTax)} />
          <KV k="+ Non Equity (post-tax)" v={fmtINR(nonEqPostTax)} />
          <div className="h-px bg-slate-800 my-2" />
          <KV k="= Current Value" v={fmtINR(currentValue)} bold />
          <KV k="÷ Total Units" v={totalUnits.toFixed(4)} />
        </dl>
      </div>
    </div>
  );
}

function Row({ label, k, values, onChange, hint }) {
  return (
    <div>
      <Label className="text-slate-300 text-xs uppercase tracking-wider">{label}</Label>
      <Input
        type="number"
        step="0.01"
        value={values[k]}
        onChange={(e) => onChange(k, e.target.value)}
        className="mt-1.5 bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500 font-mono"
      />
      {hint && <div className="mt-1 text-[11px] text-slate-500">{hint}</div>}
    </div>
  );
}

function KV({ k, v, positive, bold }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-slate-400 text-xs">{k}</dt>
      <dd
        className={`text-sm ${bold ? "font-semibold text-white" : ""} ${
          positive === true ? "text-emerald-400" : positive === false ? "text-rose-400" : "text-slate-200"
        }`}
      >
        {v}
      </dd>
    </div>
  );
}
